import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProfileApprovalCard = ({ profile, isSelected, onToggleSelect, onApprove, onReject }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleApprove = async () => {
    setIsProcessing(true);
    try {
      await onApprove(profile?.id);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason?.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    setIsProcessing(true);
    try {
      await onReject(profile?.id, rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <div className="bg-card rounded-2xl border border-border p-6 hover:border-primary/50 transition-all elevation-sm">
        <div className="flex items-start gap-4">
          {/* Selection Checkbox */}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="mt-1 w-5 h-5 rounded border-border cursor-pointer"
          />

          {/* Profile Content */}
          <div className="flex-1 space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-semibold text-foreground">{profile?.fullName}</h3>
                <p className="text-sm text-muted-foreground">{profile?.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  profile?.role === 'candidate' ?'bg-primary/10 text-primary' :'bg-secondary/10 text-secondary'
                }`}>
                  {profile?.role?.toUpperCase()}
                </span>
                {profile?.qualificationScore && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                    Score: {profile?.qualificationScore}
                  </span>
                )}
              </div>
            </div>

            {/* Profile Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profile?.companyName && (
                <div className="flex items-center gap-2">
                  <Icon name="Building2" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Company</p>
                    <p className="text-sm font-medium text-foreground">{profile?.companyName}</p>
                  </div>
                </div>
              )}
              {profile?.industry && (
                <div className="flex items-center gap-2">
                  <Icon name="Briefcase" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Industry</p>
                    <p className="text-sm font-medium text-foreground">{profile?.industry}</p>
                  </div>
                </div>
              )}
              {profile?.seniority && (
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Seniority</p>
                    <p className="text-sm font-medium text-foreground">{profile?.seniority}</p>
                  </div>
                </div>
              )}
              {profile?.experience && (
                <div className="flex items-center gap-2">
                  <Icon name="Award" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Experience</p>
                    <p className="text-sm font-medium text-foreground">{profile?.experience}</p>
                  </div>
                </div>
              )}
              {profile?.location && (
                <div className="flex items-center gap-2">
                  <Icon name="MapPin" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Location</p>
                    <p className="text-sm font-medium text-foreground">{profile?.location}</p>
                  </div>
                </div>
              )}
              {profile?.language && (
                <div className="flex items-center gap-2">
                  <Icon name="Globe" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Language</p>
                    <p className="text-sm font-medium text-foreground">{profile?.language}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Bio */}
            {profile?.bio && (
              <div className="bg-background/50 rounded-xl p-4">
                <p className="text-xs text-muted-foreground mb-1">Bio</p>
                <p className="text-sm text-foreground">{profile?.bio}</p>
              </div>
            )}

            {/* Admin Notes */}
            {profile?.adminNotes && (
              <div className="bg-warning/5 border border-warning/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="AlertCircle" size={14} className="text-warning" />
                  <p className="text-xs font-medium text-warning">Admin Notes</p>
                </div>
                <p className="text-sm text-foreground">{profile?.adminNotes}</p>
              </div>
            )}

            {/* Metadata */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="Calendar" size={12} />
                <span>Submitted: {formatDate(profile?.submittedAt)}</span>
              </div>
              {profile?.linkedinUrl && (
                <a
                  href={profile?.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <Icon name="Linkedin" size={12} />
                  <span>LinkedIn Profile</span>
                </a>
              )}
              {profile?.companyWebsite && (
                <a
                  href={profile?.companyWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <Icon name="ExternalLink" size={12} />
                  <span>Company Website</span>
                </a>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                onClick={handleApprove}
                loading={isProcessing}
                iconName="CheckCircle"
                className="flex-1 bg-success hover:bg-success/90 text-white"
              >
                Approve Profile
              </Button>
              <Button
                onClick={() => setShowRejectModal(true)}
                variant="outline"
                iconName="XCircle"
                className="flex-1 border-error text-error hover:bg-error/10"
              >
                Reject Profile
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 max-w-md w-full elevation-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="XCircle" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Reject Profile</h3>
                <p className="text-sm text-muted-foreground">Provide a reason for rejection</p>
              </div>
            </div>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e?.target?.value)}
              placeholder="Enter rejection reason..."
              className="w-full h-32 px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />

            <div className="flex gap-3 mt-4">
              <Button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                variant="outline"
                fullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                loading={isProcessing}
                fullWidth
                className="bg-error hover:bg-error/90 text-white"
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileApprovalCard;